import React from 'react';
import { FaLock, FaArrowRight } from 'react-icons/fa';

/**
 * AI Feature Card Component
 * Follows Single Responsibility Principle - displays feature information
 * Follows Interface Segregation - specific props for feature display
 * 
 * @param {Object} feature - Feature data
 * @param {boolean} isLocked - Whether feature is locked
 * @param {Function} onClick - Callback when card is clicked
 * @param {Object} usageInfo - {used, limit, percentage}
 * @param {string} tierBadge - Tier requirement (FREE, BASIC, PREMIUM)
 */
const FeatureCard = ({ 
  feature, 
  isLocked = false, 
  onClick, 
  usageInfo = null,
  tierBadge = 'FREE'
}) => {
  // Tier color mapping
  const getTierBadgeColor = (tier) => {
    switch (tier?.toUpperCase()) {
      case 'PREMIUM':
        return 'bg-gradient-to-r from-purple-600 to-blue-600 text-white';
      case 'BASIC':
        return 'bg-blue-600 text-white';
      case 'FREE':
        return 'bg-green-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  // Get usage bar color based on percentage
  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div
      onClick={!isLocked ? onClick : null}
      className={`relative bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform ${
        isLocked 
          ? '' 
          : 'hover:-translate-y-1 cursor-pointer'
      } border border-gray-200 overflow-hidden`}
    >
      {/* Lock Overlay */}
     

      <div className="p-6">
        {/* Header: Icon, Name, and Badge */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Feature Icon */}
            <div className={`text-3xl ${isLocked ? 'grayscale' : ''}`}>
              {feature?.icon || '🧠'}
            </div>
            
            <div>
              {/* Feature Name */}
              <h3 className="text-lg font-bold text-gray-800">
                {feature?.name || 'Feature'}
              </h3>
            </div>
          </div>

          {/* Tier Badge */}
          <span className={`${getTierBadgeColor(tierBadge)} px-2 py-1 rounded text-xs font-semibold flex items-center gap-1`}>
            {tierBadge}
          </span>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {feature?.description || 'AI-powered feature to enhance your learning experience'}
        </p>

        {/* Usage Meter (if applicable) */}
        {usageInfo && !isLocked && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>Used: {usageInfo.used || 0}/{usageInfo.limit || 0} this month</span>
              <span>{usageInfo.percentage || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${getUsageColor(usageInfo.percentage || 0)}`}
                style={{ width: `${Math.min(usageInfo.percentage || 0, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* CTA Button */}
        {!isLocked ? (
          <button
            onClick={onClick}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors duration-200"
          >
            <span>{feature?.ctaText || 'Use Feature'}</span>
            <FaArrowRight className="text-sm" />
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              // This will be handled by parent component to show upgrade modal
              if (onClick) onClick();
            }}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-200"
          >
            <FaLock className="text-sm" />
            <span>Upgrade to {tierBadge}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default FeatureCard;
